/**
 * VARExportService
 * Export functionality for VAR Explorer data
 * Issue: #290
 */

import { VAREntity } from '../../../types/var';

/**
 * Service class for VAR data export operations
 * Supports CSV and GeoJSON export formats
 */
export class VARExportService {
  /**
   * Export VAR data to CSV format
   * @param data Array of VAR entities to export
   * @returns Blob containing CSV data
   */
  async exportToCsv(data: VAREntity[]): Promise<Blob> {
    const headers = [
      'VAR ID',
      'Name',
      'Type',
      'Subtype',
      'Region',
      'Status',
      'Occupancy',
      'Latitude',
      'Longitude',
      'Red Flag',
      'Sprinkler',
      'Verified Date'
    ];

    const csvRows = [
      headers.join(','),
      ...data.map(entry => [
        this.escapeCsvCell(entry.var_id),
        this.escapeCsvCell(entry.name),
        this.escapeCsvCell(entry.var_type),
        this.escapeCsvCell(entry.var_subtype),
        this.escapeCsvCell(entry.region),
        this.escapeCsvCell(entry.status),
        this.escapeCsvCell(entry.occupancy),
        this.escapeCsvCell(this.getLatitude(entry)),
        this.escapeCsvCell(this.getLongitude(entry)),
        this.escapeCsvCell(entry.red_flag_active ? 'Yes' : 'No'),
        this.escapeCsvCell(entry.sprinklerFlag?.active ? 'Yes' : 'No'),
        this.escapeCsvCell(entry.verification?.Date)
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  }

  /**
   * Export VAR data to GeoJSON format
   * Only includes VARs that have location data
   * @param data Array of VAR entities to export
   * @returns Blob containing GeoJSON data
   */
  async exportToGeoJson(data: VAREntity[]): Promise<Blob> {
    // Filter out VARs without location
    const varsWithLocation = data.filter(entry => entry.location !== null);

    const featureCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: varsWithLocation.map(entry => ({
        type: 'Feature',
        geometry: entry.location as GeoJSON.Point,
        properties: {
          var_id: entry.var_id,
          name: entry.name,
          var_type: entry.var_type,
          status: entry.status,
          region: entry.region
        }
      }))
    };

    const geoJsonContent = JSON.stringify(featureCollection, null, 2);
    return new Blob([geoJsonContent], { type: 'application/geo+json' });
  }

  /**
   * Trigger browser download of a blob
   * @param blob The blob to download
   * @param filename The filename for the download
   */
  download(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  /**
   * Escape a value for CSV format
   * Wraps in quotes and escapes internal quotes
   */
  private escapeCsvCell(value: string | null | undefined): string {
    if (value === null || value === undefined) {
      return '""';
    }
    // Convert to string and escape internal quotes by doubling them
    const stringValue = String(value);
    const escaped = stringValue.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  /**
   * Extract latitude from VAREntity location
   */
  private getLatitude(entry: VAREntity): string | null {
    if (!entry.location || !entry.location.coordinates) {
      return null;
    }
    // GeoJSON Point coordinates are [longitude, latitude]
    return String(entry.location.coordinates[1]);
  }

  /**
   * Extract longitude from VAREntity location
   */
  private getLongitude(entry: VAREntity): string | null {
    if (!entry.location || !entry.location.coordinates) {
      return null;
    }
    // GeoJSON Point coordinates are [longitude, latitude]
    return String(entry.location.coordinates[0]);
  }
}

// Export singleton instance
export const varExportService = new VARExportService();
